import { faBars, faExternalLink, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { isArray } from "lodash";
import React from "react";
import { FocusScope } from "react-aria";
import { Link, useParams } from "react-router-dom";
import { Button, Container, Divider, Menu, Popup, Segment } from "semantic-ui-react";
import { Flyout, useFlyout } from "../../../../../../components/global_components/Flyout";
import { useClient } from "../../../../../../hooks/use-client";
import identity from "../../../../../../selectors/identity";
import { ContactAvatar } from "../../../../../permits/components/ContactAvatar";

export function PublicTopNavigation({ children, defaultLink }: React.PropsWithChildren<{ defaultLink: string }>) {
  const { site } = useParams<{ site: string }>();
  const { query } = useClient(site);
  const siteSeal = query.data?.payload?.data?.attributes?.seal;
  const siteName = query.data?.payload?.data?.attributes?.name;

  const userFirstName = identity.getUserFirstName() ?? "";
  const userLastName = identity.getUserLastName() ?? "";
  const isLoggedIn = identity.isLoggedIn();

  const { isOpen, open, close } = useFlyout();

  return (
    <>
      <MobileLinksFlyout isOpen={isOpen} onClose={close} defaultLink={defaultLink}>
        {children}
      </MobileLinksFlyout>
      <div className='tw-z-100 tw-h-[4.5rem] tw-border-x-0 tw-border-b-[1px] tw-border-t-0 tw-border-solid tw-border-grey-light tw-bg-white tw-shadow-md'>
        <Container className='tw-flex tw-h-full'>
          <div className='tw-flex tw-h-full tw-w-full tw-items-center tw-justify-between'>
            <Link to={defaultLink}>
              <div className='tw-flex tw-items-center tw-gap-4'>
                <img src={siteSeal} alt='Site Seal' className='tw-w-10 tw-rounded-full' />
                <span className='tw-whitespace-nowrap tw-text-lg tw-text-black'>{siteName}</span>
              </div>
            </Link>
            <div className='tw-hidden tw-items-center tw-gap-6 md:tw-flex'>
              <div className='tw-flex tw-gap-6'>{children}</div>
              {isLoggedIn && (
                <Popup
                  trigger={
                    <Button className='button-ghost tw-rounded-full tw-p-1'>
                      <ContactAvatar firstName={userFirstName} lastName={userLastName} />
                    </Button>
                  }
                  on={"click"}
                  position='bottom right'
                  basic
                  offset={[4, 10]}
                  className='tw-overflow-hidden tw-p-0'
                >
                  <FocusScope autoFocus contain restoreFocus>
                    <UserCard defaultLink={defaultLink} />
                  </FocusScope>
                </Popup>
              )}
              {!isLoggedIn && (
                <div className='tw-flex tw-gap-6'>
                  <Button
                    className='button-ghost tw-m-0'
                    as={Link}
                    to={{
                      pathname: `/${site}/public/login`,
                      search: `?redirect=${defaultLink}`,
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    primary
                    className='tw-m-0'
                    as={Link}
                    to={{
                      pathname: `/${site}/public/register`,
                      search: `?redirect=${defaultLink}`,
                    }}
                  >
                    Sign Up
                  </Button>
                </div>
              )}
            </div>
            <div className='tw-flex tw-items-center tw-justify-center md:tw-hidden'>
              <Button
                className='button-ghost black tw-m-0 tw-flex tw-items-center tw-justify-center tw-p-3'
                onClick={open}
              >
                <FontAwesomeIcon className='tw-h-6 tw-w-6' icon={faBars} />
              </Button>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
}

PublicTopNavigation.Link = function PublicTopNavigationLink({
  children,
  to,
  externalRedirect,
  activeWhenURLIncludes,
}: React.PropsWithChildren<{
  to: string | { pathname: string; search: string };
  externalRedirect?: boolean;
  activeWhenURLIncludes: string | string[];
}>) {
  const { site } = useParams<{ site: string }>();
  const isActive = isActiveTab(activeWhenURLIncludes);

  return (
    <div>
      <Link
        className={classNames(
          "tw-flex tw-items-center tw-justify-center tw-rounded-full tw-px-6 tw-py-2 tw-text-base/6 tw-text-black tw-transition-all tw-duration-75",
          {
            "tw-bg-[#004cd015] active:tw-bg-[#1c51ac2b]": isActive,
            "hover:tw-bg-[#004cd015] active:tw-bg-[#1c51ac2b]": !isActive,
            "tw-font-bold": isActive,
          },
        )}
        to={`/${site}/public${to}`}
        target={externalRedirect ? "_blank" : undefined}
      >
        {children}
      </Link>
    </div>
  );
};

function MobileLinksFlyout({
  children,
  isOpen,
  onClose,
  defaultLink,
}: React.PropsWithChildren<{ isOpen: boolean; onClose: () => void; defaultLink: string }>) {
  const { site } = useParams<{ site: string }>();
  const isLoggedIn = identity.isLoggedIn();

  return (
    <Flyout open={isOpen} onClose={onClose}>
      <Flyout.Header className='tw-bg-transparent tw-p-0'>
        <div className='tw-flex tw-w-full tw-items-center tw-justify-end tw-px-3'>
          <Button
            className='button-ghost black tw-m-0 tw-flex tw-items-center tw-justify-center tw-p-3'
            onClick={onClose}
          >
            <FontAwesomeIcon className='tw-h-6 tw-w-6' icon={faXmark} />
          </Button>
        </div>
      </Flyout.Header>
      <Flyout.Body>
        <div className='tw-space-y-1'>{children}</div>
      </Flyout.Body>
      <div className='tw-p-3'>
        {isLoggedIn && (
          <Segment className='tw-p-0'>
            <UserCard defaultLink={defaultLink} />
          </Segment>
        )}
        {!isLoggedIn && (
          <>
            <Divider className='tw-my-3 tw-border-x-0 tw-border-dashed' />
            <div className='tw-flex tw-gap-2'>
              <Button
                className='button-ghost tw-m-0'
                as={Link}
                to={{
                  pathname: `/${site}/public/login`,
                  search: `?redirect=${defaultLink}`,
                }}
                fluid
              >
                Sign In
              </Button>
              <Button
                primary
                className='tw-m-0'
                as={Link}
                to={{
                  pathname: `/${site}/public/register`,
                  search: `?redirect=${defaultLink}`,
                }}
                fluid
              >
                Sign Up
              </Button>
            </div>
          </>
        )}
      </div>
    </Flyout>
  );
}

function UserCard({ defaultLink }: { defaultLink: string }) {
  const { site } = useParams<{ site: string }>();
  const userFirstName = identity.getUserFirstName() ?? "";
  const userLastName = identity.getUserLastName() ?? "";
  const userFullName = [userFirstName, userLastName].filter(Boolean).join(" ").trim();
  const userEmail = identity.getUserEmail();

  return (
    <>
      <div className='tw-flex tw-items-center tw-gap-2 tw-p-3'>
        <ContactAvatar firstName={userFirstName} lastName={userLastName} />
        <div>
          <div className='tw-m-0 tw-font-bold'>{userFullName}</div>
          <div className='tw-text-grey-dark tw-text-sm'>{userEmail}</div>
        </div>
      </div>
      <Divider className='tw-my-0' />
      <Menu vertical borderless className='tw-m-0 tw-w-full tw-space-y-1 tw-border-none tw-p-3 tw-shadow-none'>
        <Menu.Item
          as={Link}
          to={`/${site}/public/account`}
          target='_blank'
          className='tw-flex tw-justify-between tw-px-4 tw-py-3'
        >
          <span>My Account</span>
          <FontAwesomeIcon icon={faExternalLink} />
        </Menu.Item>
        <Menu.Item
          as={Link}
          to={{
            pathname: `/${site}/logout`,
            search: `?redirect=${defaultLink}`,
          }}
          className=' tw-px-4 tw-py-3'
        >
          Logout
        </Menu.Item>
      </Menu>
    </>
  );
}

function isActiveTab(match: string | string[]) {
  if (isArray(match)) {
    return match.some((url) => location.pathname.includes(url));
  }
  return location.pathname.includes(match);
}
